import React, { useRef, useState, useEffect } from 'react';
import { FramingMode, FramingRow } from '@/types/contentFraming';
import { Popover } from 'antd';
import { InfoCircleOutlined, DownOutlined, UnorderedListOutlined, OrderedListOutlined } from '@ant-design/icons';
import { useContentFramingStore } from '@/state/contentFramingStore';
import Copilot from './Copilot';
import { applyBulletToggle, applyNumberingToggle, handleListKeyDownLogic } from './textListEditing';

type Props = {
  row: FramingRow;
  placeholderOptions: { value: string; label: string }[];
  notesTrue?: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onSelectionChange: (id: string, value: string) => void;
  onInputTextChange: (id: string, value: string) => void;
  onModeChange?: (id: string, mode: FramingMode) => void;
  onUpdateFieldsFromShape: (id: string, shapeId: string) => void;
  onHoverShape: (shapeId: string | null) => void;
  formatting?: boolean;
  keyTakeAways?: boolean;
  script?: boolean;
};

const Pill: React.FC<{
  value: FramingMode;
  label: string;
  selected: boolean;
  disabled: boolean;
  onChange: (value: FramingMode) => void;
}> = ({ value, label, selected, disabled, onChange }) => {
  const base = 'px-2 py-1 rounded-sm border text-xs transition-colors';
  const selectedCls = selected
    ? 'bg-white border-as-orange-primary text-as-orange-primary font-bold'
    : 'bg-white text-black border-gray-300 hover:bg-gray-100';
  const disabledCls = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  return (
    <button
      type="button"
      className={`${base} ${selectedCls} ${disabledCls}`}
      onClick={() => !disabled && onChange(value)}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

const ContentFramingDynamic: React.FC<Props> = ({
  row,
  placeholderOptions,
  formatting = true,
  onToggle,
  onSelectionChange,
  onInputTextChange,
  onModeChange = () => {},
  onUpdateFieldsFromShape,
  onHoverShape,
  keyTakeAways = false,
  script = false,
}) => {
  const isDisabled = !row.enabled;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const slideShapesData = useContentFramingStore((s) => s.slideShapesData);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rowContainerRef = useRef<HTMLDivElement>(null);
  const openCopilot = useContentFramingStore((s) => s.openCopilot);
  const closeCopilot = useContentFramingStore((s) => s.closeCopilot);
  const copilot = useContentFramingStore((s) => s.copilot);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        onHoverShape(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onHoverShape]);

  useEffect(() => {
    if (copilot.mode === '') return;
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const inCopilot = !!target.closest(`[data-row-id="${row.id}"]`);
      const inInputs = !!target.closest('[data-copilot-input="true"]');
      if (inCopilot || inInputs) return;
      closeCopilot();
    };
    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [copilot.mode, row.id]);

  const insertBullet = () => {
    if (!textareaRef.current || isDisabled) return;
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const { newText, newCursorPos } = applyBulletToggle(row.inputText, start, end);
    onInputTextChange(row.id, newText);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const insertNumbering = () => {
    if (!textareaRef.current || isDisabled) return;
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const { newText, newCursorPos } = applyNumberingToggle(row.inputText, start, end);
    onInputTextChange(row.id, newText);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isDisabled) return;
    
    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = row.inputText;
    
    if (e.key === 'Enter') {
      const result = handleListKeyDownLogic(text, start, end, 'Enter');
      if (result.handled) {
        e.preventDefault();
        onInputTextChange(row.id, result.newText!);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(result.newCursorPos!, result.newCursorPos!);
            textareaRef.current.focus();
          }
        }, 0);
        return;
      }
    }
    
    // Handle Backspace at start of bulleted/numbered line
    if (e.key === 'Backspace' && start === end && start > 0) {
      const result = handleListKeyDownLogic(text, start, end, 'Backspace');
      if (result.handled) {
        e.preventDefault();
        onInputTextChange(row.id, result.newText!);
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(result.newCursorPos!, result.newCursorPos!);
            textareaRef.current.focus();
          }
        }, 0);
        return;
      }
    }
  };

  return (
    <div className="flex flex-col w-full gap-12 py-2 mb-3" ref={rowContainerRef}>
      {/* Left column: toggle + framing + placeholder + current text */}
      <div className="flex items-start gap-3 w-full">
        <div className="flex items-start flex-col gap-1 w-[8%] min-w-[60px]">
          <div className="text-sm font-bold text-gray-700 border-b border-black">Incl.</div>
          <input
            type="checkbox"
            id={`enable-${row.id}`}
            checked={row.enabled}
            onChange={(e) => onToggle(row.id, e.target.checked)}
            className="w-4 h-4 text-black border-as-orange-primary rounded focus:ring-as-orange-primary accent-as-orange-primary"
          />
        </div>

        <div className="flex items-start gap-1 flex-col w-[20%]" onClick={() => onHoverShape(row.selection)}>
            <div className="text-sm font-bold text-gray-700 border-b border-black">Framing Type</div>
            <div className="text-sm  text-gray-700">{row.label}</div>
        </div>

        {!script && (
        <div className="flex items-start gap-1 flex-col w-[30%] min-w-[200px]">
            <div className="text-sm font-bold text-gray-700 border-b border-black">Placeholder Selected</div>
            <div className="relative w-4/5" ref={dropdownRef}>
                <div
                    onClick={() => {
                    if (isDisabled) return;
                    setIsDropdownOpen(!isDropdownOpen);
                    if (!isDropdownOpen) onHoverShape(row.selection);
                    }}
                    tabIndex={isDisabled ? -1 : 0}
                    className={`w-full px-2 border rounded-lg text-sm transition-colors cursor-pointer focus:outline-none flex items-center justify-between ${
                    isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:border-gray-400 focus:border-black'
                    }`}
                >
                    <span className="truncate pr-2">{
                      (row.selection && (slideShapesData.find(s => s.id === row.selection)?.label))
                      || placeholderOptions?.find(option => option.value === row.selection)?.label
                      || 'Select a placeholder'
                    }</span>
                    <DownOutlined className="text-gray-500 text-xs" />
                </div>
                {isDropdownOpen && !isDisabled && (
                    <div className="absolute left-0 right-0 z-50 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto mt-1">
                    {placeholderOptions?.map((opt, idx) => (
                        <div
                        key={opt.value}
                        className={`px-3 py-2 text-sm cursor-pointer ${focusedIndex === idx ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'}`}
                        onClick={() => {
                            onSelectionChange(row.id, opt.value);
                            if (opt.value) onUpdateFieldsFromShape(row.id, opt.value);
                            setIsDropdownOpen(false);
                            setFocusedIndex(-1);
                        }}
                        onMouseEnter={() => {
                            setFocusedIndex(idx);
                            if (opt.value) onHoverShape(opt.value);
                        }}
                        onMouseLeave={() => onHoverShape(null)}
                        >
                        {opt.label}
                        </div>
                    ))}
                    </div>
                )}
            </div>
        </div>
        )}
        {/* Right column: formatting pills + updated text */}
        {formatting && (
        <div className="flex items-start gap-1 w-[42%]">
            <div className="flex items-start gap-1 flex-col w-full">
                <div className="text-sm font-bold text-gray-700 border-b border-black flex items-center gap-1">Formatting 
                <Popover overlayStyle={{ width: 300 }} content="This selection controls the format of the “Updated Text to Include” text that will be added to the associated placeholder. For each Framing Type, your selected Format Template has pre-designed text size, font, color, and emphasis characteristics. You can choose to use that Format Template or simply use the text formatting attributed with the Placeholder Design on your current slide." trigger="click" placement="bottom">
                  <InfoCircleOutlined className="text-black cursor-pointer" />
                </Popover>  
                </div>
                <div className="flex items-start gap-1" role="radiogroup" aria-label={`Mode selection for ${row.label}`} onClick={() => onHoverShape(row.selection)}> 
                    <Pill
                    value="placeholderDesign"
                    label="Placeholder Design"
                    selected={row.mode === 'placeholderDesign'}
                    disabled={isDisabled}
                    onChange={(mode) => onModeChange(row.id, mode)}
                    />
                     <Pill
                    value="formatTemplate"
                    label="Format Template"
                    selected={row.mode === 'formatTemplate'}
                    disabled={isDisabled}
                    onChange={(mode) => onModeChange(row.id, mode)}
                    />
                </div>
            </div>
        </div>
        )}


      

        
      </div>
      <div className="flex items-end gap-3 w-full">

            <div className="flex items-start gap-1 flex-col w-1/2 pr-2" onClick={() => onHoverShape(row.selection)}>
                <div className="text-xs font-bold text-gray-700 border-b border-black">Current Text</div>
                
                    <textarea
                    value={row.disabledText}
                    readOnly
                    disabled
                    rows={ keyTakeAways || script ? 9 : 3}
                    className="w-full px-2 py-2 border rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed resize-none"
                    />
               
            </div>
            <div className="flex items-start justify-end gap-1 flex-col w-1/2 pl-2">

            {/* Bullet and Numbering for Key Take-Aways and Script */}
            <div className="flex items-center justify-between gap-2 w-full">
              <div className="text-xs font-bold text-gray-700">Bullet/Numbering: </div>
              {(keyTakeAways || script) && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={insertBullet}
                    disabled={isDisabled}
                    className={`p-1.5 rounded border transition-colors ${
                      isDisabled 
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 border-gray-300' 
                        : 'bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100'
                    }`}
                    title="Insert bullet point"
                  >
                    <UnorderedListOutlined className="text-gray-700 text-base" />
                  </button>
                  <button
                    type="button"
                    onClick={insertNumbering}
                    disabled={isDisabled}
                    className={`p-1.5 rounded border transition-colors ${
                      isDisabled 
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 border-gray-300' 
                        : 'bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100'
                    }`}
                    title="Insert numbering"
                  >
                    <OrderedListOutlined className="text-gray-700 text-base" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between w-full mb-1">
              <div className="text-xs font-bold text-gray-700 border-b border-black">Updated Text to include</div>
             
            </div>

            
                <textarea
                ref={textareaRef}
                value={row.inputText}
                onChange={(e) => onInputTextChange(row.id, e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isDisabled}
                rows={ keyTakeAways || script ? 9 : 3}
                placeholder="Enter text..."
                className={`w-full px-2 py-2 border rounded-lg text-sm transition-colors resize-none ${
                isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:border-gray-400 focus:border-black focus:ring-1 focus:ring-black'
                }`}
                onFocus={(e) => {
                  onHoverShape(row.selection); 
                  const rect = e.currentTarget.getBoundingClientRect();
                  openCopilot(row.id, rect.left - 26, rect.bottom - 2);
                }}
                onBlur={() => onHoverShape(null)}
                />
            
          </div>
        </div>
        {/* Floating Co-Pilot icon */}
      <Copilot 
        tooltip="Alpha Agent" 
        onClose={() => closeCopilot()}
      />
    </div>
  );
};

export default ContentFramingDynamic;


